/**
 * LoadAdvertisersButton -- reusable loader button for advertiser discovery.
 *
 * Renders a button that POSTs credential values from the surrounding form to a
 * configured discovery endpoint (e.g. Yahoo DSP's
 * ``/connectors/yahoo_dsp/advertisers``). On success the resulting advertiser
 * list is forwarded to ``onLoaded`` so a sibling field (typically a dynamic
 * multi-select checkbox) can populate its options.
 *
 * The component is state-driven:
 *   - ``idle``     Initial state. Button disabled until all declared credential
 *                  fields in ``formValues`` are non-empty.
 *   - ``loading``  Request in flight. Button disabled with a spinner.
 *   - ``loaded``   Most recent request succeeded. Shows a "N advertisers
 *                  loaded" confirmation alongside the button.
 *   - ``error``    Most recent request failed. Renders a human-readable error
 *                  keyed off the backend error code (see ERROR_MESSAGES).
 *
 * Uses ``apiClient`` (not raw fetch) so authentication, CSRF, and the
 * ``/api/v1`` prefix are handled consistently with the rest of the admin UI.
 */

import { useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { apiClient, ApiError } from '@/lib/api/client';

/** Shape of a single advertiser entry returned from the discovery endpoint. */
export type Advertiser = {
  id: number;
  name: string;
  status: string;
  currency?: string;
  timezone?: string;
};

export interface LoadAdvertisersButtonProps {
  /** Internal field name (used as a data attribute for integration/debugging). */
  fieldName: string;
  /**
   * Discovery endpoint path. Do NOT prefix with ``/api/v1`` -- ``apiClient``
   * already sets that as its baseURL.
   */
  endpoint: string;
  /**
   * Names of sibling form fields whose values should be sent as the POST body.
   * The button is disabled until every named field has a non-empty value in
   * ``formValues``.
   */
  credentialFields: string[];
  /** Current form values. Keyed by field name. */
  formValues: Record<string, string>;
  /** Callback fired with the advertiser list on a successful load. */
  onLoaded: (advertisers: Advertiser[]) => void;
  /** When true (a selection already exists), present the button as a Refresh. */
  hasSelection?: boolean;
  /**
   * Saved configuration name. Sent as ``connector_name`` when
   * ``sendsConnectorName`` is set, so the endpoint can resolve the stored
   * credentials the browser never receives (#1999).
   */
  connectorName?: string;
  /** Declared by the field's ``loader_config.sends_connector_name`` (#1999). */
  sendsConnectorName?: boolean;
}

type Status = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Build a comparable snapshot key from the credential field values. Used to
 * detect when the user has edited credentials AFTER a successful load so the
 * UI can warn that the advertiser list may be stale.
 *
 * Uses ``\u0000`` as a separator so values cannot collide by concatenation.
 */
function credsSnapshot(
  fields: string[],
  values: Record<string, string>,
): string {
  return fields.map((f) => values[f] ?? '').join('\u0000');
}

/** Last resort: neither a known code nor a message from the server. */
const GENERIC_FAILURE = 'The advertiser lookup failed. Try again in a minute.';

/**
 * Map of backend error codes (from ``detail.error``) to user-facing messages.
 * Codes mirror those emitted by the Yahoo DSP and Beeswax discovery routes;
 * a code with no entry here falls back to the server's own message.
 *
 * ``advertiser_shape_unreadable`` has no generic paraphrase on purpose
 * (#1999): the server's message names the fields the provider actually
 * returned, and that is the whole value of the error.
 */
const ERROR_MESSAGES: Record<string, string> = {
  missing_credentials: 'Please fill in this connector\'s credentials.',
  invalid_credentials:
    'Credentials rejected by the provider. Check them and try again.',
  yahoo_api_error:
    'Yahoo DSP returned an error. Try again, or contact your Yahoo rep.',
  yahoo_api_unreachable: 'Yahoo DSP is unreachable. Try again in a minute.',
  rate_limited: 'The provider rate limited the request. Try again shortly.',
  beeswax_api_error:
    'Beeswax returned an error. Try again, or check the Buzz host.',
  beeswax_api_unreachable: 'Beeswax is unreachable. Try again in a minute.',
  configuration_not_found:
    'Save this configuration first, then load its advertisers.',
  credentials_undecryptable:
    'The stored credentials could not be decrypted. Re-enter and save them.',
};

/** Pull the server's own message out of an ApiError, when it sent one. */
function extractErrorDetail(err: unknown): string | null {
  if (err instanceof ApiError) {
    const details = err.details as { detail?: string } | string | undefined;
    if (details && typeof details === 'object' && typeof details.detail === 'string') {
      return details.detail;
    }
  }
  return null;
}

/** Pull a backend error code out of an ApiError's details payload. */
function extractErrorCode(err: unknown): string {
  if (err instanceof ApiError) {
    const details = err.details as { error?: string } | string | undefined;
    if (details && typeof details === 'object' && typeof details.error === 'string') {
      return details.error;
    }
    if (err.status === 401) return 'invalid_credentials';
    if (err.status === 429) return 'rate_limited';
    if (err.status === 400) return 'missing_credentials';
    if (err.status === 504) return 'yahoo_api_unreachable';
    if (err.status === 502) return 'yahoo_api_error';
  }
  return 'yahoo_api_unreachable';
}

export function LoadAdvertisersButton({
  fieldName,
  endpoint,
  credentialFields,
  formValues,
  onLoaded,
  hasSelection,
  connectorName,
  sendsConnectorName,
}: LoadAdvertisersButtonProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  // Snapshot of credential values captured on the last successful load.
  // When the current snapshot no longer matches, the advertiser list shown
  // elsewhere in the form is stale.
  const [lastLoadedCreds, setLastLoadedCreds] = useState<string | null>(null);

  // Either the form can supply the credentials, or the server can.
  //
  // On the Configuration page it is ALWAYS the server: credential fields are
  // returned to the browser as `has_*` flags, never as values, so requiring
  // form values would leave this button permanently disabled on exactly the
  // surface #1999 was raised from. `has_*` flags are not a substitute -- the
  // flag set is a fixed list (password, tokens, client_id/secret, api_key) and
  // carries nothing for `buzz_host` or `email`.
  //
  // Nothing is assumed about the stored credentials being usable: when they
  // are absent or rejected the endpoint answers with a named error, which is
  // what acceptance 3 asks for and what a disabled button cannot express.
  const serverCanResolveCreds = Boolean(sendsConnectorName && connectorName);
  const credsComplete =
    serverCanResolveCreds ||
    credentialFields.every((f) => (formValues[f] ?? '').trim().length > 0);
  const isLoading = status === 'loading';
  const disabled = isLoading || !credsComplete;

  const currentCreds = credsSnapshot(credentialFields, formValues);
  const isStale =
    status === 'loaded' &&
    lastLoadedCreds !== null &&
    currentCreds !== lastLoadedCreds;

  const handleClick = async () => {
    setStatus('loading');
    setError(null);

    // Build POST body from the declared credential fields. Missing values
    // pass as empty strings so the server can emit a clear validation error.
    const body: Record<string, string> = {};
    for (const f of credentialFields) {
      body[f] = formValues[f] ?? '';
    }
    // Lets the server sign in with this configuration's stored credentials
    // (#1999). It uses them as one set and ignores the fields above, so a typed
    // host never receives the stored password (#2284).
    if (sendsConnectorName && connectorName) {
      body.connector_name = connectorName;
    }

    try {
      const response = await apiClient.post(endpoint, body);
      const payload = response.data ?? {};
      const advertisers: Advertiser[] = Array.isArray(payload.advertisers)
        ? payload.advertisers
        : [];
      setCount(advertisers.length);
      setStatus('loaded');
      setLastLoadedCreds(credsSnapshot(credentialFields, formValues));
      onLoaded(advertisers);
    } catch (err) {
      setStatus('error');
      setError(
        ERROR_MESSAGES[extractErrorCode(err)] ??
          // No mapping for this code: show what the server said rather than a
          // generic line that would hide a named shape error (#1999).
          extractErrorDetail(err) ??
          GENERIC_FAILURE,
      );
    }
  };

  return (
    <div data-field-name={fieldName} className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleClick}
          disabled={disabled}
          aria-label="Load Advertisers"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              Loading...
            </>
          ) : hasSelection ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              Refresh advertisers
            </>
          ) : (
            'Load Advertisers'
          )}
        </Button>

        {status === 'loaded' && count !== null && !isStale && (
          <span
            className="inline-flex items-center gap-1 text-sm text-success"
            role="status"
            aria-live="polite"
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {count} advertiser{count === 1 ? '' : 's'} loaded
          </span>
        )}

        {isStale && (
          <span
            className="inline-flex items-center gap-1 text-sm text-warning"
            role="status"
            aria-live="polite"
          >
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Credentials changed -- reload advertisers
          </span>
        )}
      </div>

      {status === 'error' && error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span role="alert">{error}</span>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
